import React from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { FontFamily } from '../../../../../constants/fonts';
import type { WesHierarchyLevel } from '../useWesPalletStagingFilters';
import { formatApiCount, formatApiPercent, type WesGeoBar } from '../wesPalletStagingMappers';
import type { WesStagingScope } from '../wesPalletStagingStaticRows';
import WesSectionState, { type WesSectionStatus } from './WesSectionState';
import { WesTooltipTarget } from './WesTooltip';
import { WES_CARD, WES_CARD_SUBTITLE, WES_CARD_TITLE, WES_CARD_TITLE_SUFFIX } from './wesCardStyle';

const LEVEL_TITLE: Record<WesHierarchyLevel, string> = {
  region: 'Region / Unit',
  market: 'Market / POD',
  location: 'Location',
};

export const hierarchyTitle = (level: WesHierarchyLevel) => LEVEL_TITLE[level];

const SCOPE_TIP: Record<WesStagingScope, string> = {
  all: 'all staged',
  audit: 'audit-related staged',
  other: 'other staged',
};

// The list scrolls vertically once it grows past this height.
const LIST_MAX_HEIGHT = 330;

interface WesGeographyBarsProps {
  groups: WesGeoBar[];
  level: WesHierarchyLevel;
  scope: WesStagingScope;
  status?: WesSectionStatus;
}

// All groups are shown (no top-ten cap), highest % staged first; bars are scaled to the largest value.
const WesGeographyBars: React.FC<WesGeographyBarsProps> = ({ groups, level, scope, status }) => {
  const max = Math.max(...groups.map((g) => g.pct), 1);
  return (
    <View style={styles.card} testID="wes-geography">
      <Text style={styles.title} testID="wes-geography-title">
        {'% of Pallets Staged'}
        <Text style={styles.suffix}>{` by ${LEVEL_TITLE[level]}`}</Text>
      </Text>
      <Text style={styles.subtitle}>Auto drill-down follows geography filters · follows Staging Scope</Text>
      <WesSectionState status={status} skeleton="chart" isEmpty={groups.length === 0} emptyMessage="No data for the current filters" testID="wes-geography">
        <ScrollView style={styles.scroll} nestedScrollEnabled showsVerticalScrollIndicator testID="wes-geography-scroll">
          <View style={styles.rows}>
            {groups.map((group, index) => {
              const tip = `${group.label}: ${formatApiCount(group.staged)} of ${formatApiCount(group.total)} pallets staged (${SCOPE_TIP[scope]})`;
              return (
                <WesTooltipTarget
                  key={group.key || group.label}
                  text={tip}
                  testID={`wes-geo-row-${group.label}`}
                  style={styles.row}
                  placement={index < 2 ? 'below' : 'above'}
                  bubbleLeft={0}
                >
                  <Text style={styles.label} numberOfLines={1}>{group.label}</Text>
                  <View style={styles.track}>
                    <View style={[styles.fill, { width: `${(group.pct / max) * 100}%` }]}>
                      <Text style={styles.value}>{formatApiPercent(group.pct)}</Text>
                    </View>
                  </View>
                </WesTooltipTarget>
              );
            })}
          </View>
        </ScrollView>
      </WesSectionState>
    </View>
  );
};

const styles = StyleSheet.create({
  card: { ...WES_CARD, flex: 1, minWidth: 0 },
  title: WES_CARD_TITLE,
  suffix: WES_CARD_TITLE_SUFFIX,
  subtitle: WES_CARD_SUBTITLE,
  scroll: { maxHeight: LIST_MAX_HEIGHT },
  rows: { rowGap: 9, paddingRight: 4 },
  row: { flexDirection: 'row', alignItems: 'center', columnGap: 12 },
  label: { width: 130, fontSize: 13, fontFamily: FontFamily.regular, color: '#616161', textAlign: 'right' },
  track: { flex: 1, height: 24, borderRadius: 4, backgroundColor: '#F3F7FD' },
  fill: { height: 24, minWidth: 44, borderRadius: 4, backgroundColor: '#034895', alignItems: 'flex-end', justifyContent: 'center', paddingRight: 8 },
  value: { fontSize: 12, fontFamily: FontFamily.bold, color: '#FFFFFF' },
});

export default WesGeographyBars;
